import React, {useCallback, useEffect, useState} from 'react';
import {
  View,
  Image,
  TextInput,
  ViewStyle,
  ImageStyle,
  TextStyle,
  Pressable,
} from 'react-native';

//ui related imports
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {Button, Icon, Table, ToggleButton, Text} from 'ui-kits';
import {SimpleDropdown} from 'ui-kits/Dropdown/SimpleDropdown';

//routing related imports
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';

import {
  employeeStatus,
  employeeFailure,
  employeeList,
  employeeTotalPages,
} from 'redux/selectors/employeeSelector';
import {getOrgID} from 'redux/selectors/authSelector';

import {
  Employee,
  getEmployeesRequest,
  resetMessage,
  setCurrentSelectedEmployee,
} from 'redux/slices/employeeSlice';

//utils imports
import {ErrorCodes, getErrorMessage} from 'utils/getMessages';

//hooks imports
import {useToast, useDebounceState, useConfimationModal} from 'hooks';
import {useDispatch, useSelector} from 'react-redux';
import {ERROR_KEY} from 'constants/keys';
import {sortOptions} from 'constants/DefaultValues';

type Column = {
  key: keyof Employee;
  label: JSX.Element | string;
  minWidth?: number;
  renderCell?: (cellData: any) => JSX.Element | null;
};

type sortTypes = keyof typeof sortOptions;

export function ViewEmployee({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const dispatch = useDispatch();
  const {showToasts} = useToast();
  const orgId = useSelector(getOrgID);
  const employees = useSelector(employeeList);
  const isLoading = useSelector(employeeStatus);
  const getEmployeeError = useSelector(employeeFailure);
  const totalPages = useSelector(employeeTotalPages);

  const [currentPage, setCurrentPage] = useState(1);
  //for status filter
  const [filterState, setFilterState] = useState<string | null>(null);
  //for date of joining filter
  const [searchText, setSearchtext] = useDebounceState<string>('', 500);
  const [sort, setSort] = useState<sortTypes>('latest');

  const [statusDropDownOpen, setStatusDropDownOpen] = useState<boolean>(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState<boolean>(false);
  const fetchEmployees = useCallback(() => {
    const filters: any[] = [];

    //adding filter for status
    if (filterState) {
      filters.push({is_active: filterState === 'active'});
    }
    const params: any = {
      org_id: orgId,
      page: currentPage,
      search_text: searchText,
      sort,
      filter: decodeURIComponent(JSON.stringify(filters)),
    };
    dispatch(getEmployeesRequest(params));
  }, [dispatch, orgId, currentPage, searchText, sort, filterState]);

  useEffect(() => {
    if (fetchEmployees) {
      fetchEmployees();
    }
  }, [fetchEmployees]);

  useEffect(() => {
    if (getEmployeeError) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(getEmployeeError as ErrorCodes),
        'error',
      );
    }
    dispatch(resetMessage());
  }, [dispatch, showToasts, getEmployeeError]);

  //handlers

  //  functionality to go to view employee screen
  const goToEmployeeDetails = (employeeID: number) => {
    dispatch(setCurrentSelectedEmployee(employeeID));
    navigation.push(Routes.VIEW_EMPLOYEE_DETAILS, {
      id: employeeID.toString(),
    });
  };

  function addEmployee() {
    navigation.navigate(Routes.ADDEMPLOYEE);
  }

  function goBack() {
    navigation.navigate(Routes.DASHBOARD);
  }

  const columns: Column[] = [
    {
      key: 'emp_id',
      label: 'Employee ID',
      renderCell: (item: Employee) => <Text>{item.emp_id}</Text>,
    },
    {
      key: 'first_name',
      label: (
        <Pressable
          style={styles.tableCellWrapper}
          onPress={() => setSortDropdownOpen(prev => !prev)}>
          <Text style={styles.tableHeader}>Full name</Text>
          <Image
            resizeMode="contain"
            style={styles.tableHeaderIcon}
            source={require('assets/icons/cheveron_down@2x.png')}
          />
          {sortDropdownOpen && (
            <SimpleDropdown
              options={Object.values(sortOptions)}
              onSelect={(value: string) => {
                const sortValue = Object.entries(sortOptions)?.find(
                  ([_, v]) => v === value,
                )![0] as sortTypes;
                setSort(sortValue);
                setSortDropdownOpen(false);
              }}
              style={styles.statusWrapper}
            />
          )}
        </Pressable>
      ),
      renderCell: (item: Employee) => (
        <View>
          <Pressable onPress={() => goToEmployeeDetails(item.emp_id)}>
            <Text style={styles.linkCellText}>
              {item.first_name} {item.last_name}
            </Text>
          </Pressable>
          {/* <View style={styles.doj}>
            {getFormattedDate(new Date(item.date_of_joining))}
          </View> */}
        </View>
      ),
    },
    {
      key: 'is_active',
      label: (
        <Pressable
          style={styles.tableCellWrapper}
          onPress={() => setStatusDropDownOpen(prev => !prev)}>
          <Text style={styles.tableHeader}>Status</Text>
          <Image
            resizeMode="contain"
            style={styles.tableHeaderIcon}
            source={require('assets/icons/cheveron_down@2x.png')}
          />
          {statusDropDownOpen && (
            <SimpleDropdown
              options={['active', 'inactive']}
              onSelect={(value: string) => {
                setFilterState(value);
                setStatusDropDownOpen(false);
              }}
              style={styles.statusWrapper}
            />
          )}
        </Pressable>
      ),
      renderCell: (item: Employee) => (
        <View>
          <ToggleButton sync value={item.is_active} />
        </View>
      ),
      minWidth: 40,
    },
    {key: 'email', label: 'Email ID'},
  ];

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.heading}>All Employee</Text>
        <Icon size={28} type="back_arrow" onPress={goBack} />
      </View>
      {/* header */}
      <View style={styles.header}>
        {/* search bar */}
        <View style={styles.searchWrapper}>
          <Image
            style={styles.icon}
            source={require('assets/icons/search@2x.png')}
          />
          <TextInput
            style={styles.searchInput}
            onChangeText={setSearchtext}
            placeholder="Search Employee by name or id"
            placeholderTextColor={colors.palette.nuetral4Placeholder}
          />
        </View>

        {/* filter button */}
        <View style={styles.headerAction}>
          {/* <ActionIconButton
            onPress={fetchEmployees}
            icon={require('assets/icons/retry@2x.png')}
          /> */}
          {/* add employee button */}
          <Button
            onPress={addEmployee}
            text="Add Employee +"
            style={styles.addButton}
          />
        </View>
      </View>
      <DisplayContent
        columns={columns}
        data={employees}
        isLoading={isLoading}
        pageHandler={setCurrentPage}
        currentPage={currentPage}
        totalPages={totalPages}
        keyExtractor={item => item.id.toString()}
      />
    </View>
  );
}

type displayContentProps = Readonly<{
  columns: Column[];
  data: Employee[];
  isLoading: boolean;
  pageHandler: (page: number) => void;
  totalPages: number;
  currentPage: number;
  keyExtractor: (item: Employee) => string;
}>;

function DisplayContent(props: displayContentProps) {
  const {
    columns,
    isLoading,
    data,
    pageHandler,
    totalPages,
    currentPage,
    keyExtractor,
  } = props;

  if (isLoading) {
    return <Text>Please wait while we load your data</Text>;
  }
  return (
    <View style={styles.tableWrapper}>
      <Table
        columns={columns}
        data={data}
        pageHandler={pageHandler}
        currentPage={currentPage}
        totalPages={totalPages}
        keyExtractor={keyExtractor}
      />
    </View>
  );
}
type styleTypes = {
  container: ViewStyle;
  searchWrapper: ViewStyle;
  icon: ImageStyle;
  searchInput: TextStyle;
  header: ViewStyle;
  headerAction: ViewStyle;
  addButton: ViewStyle;
  linkCellText: TextStyle;
  heading: TextStyle;
  doj: ViewStyle;
  tableHeader: TextStyle;
  tableHeaderIcon: ImageStyle;
  tableCellWrapper: ViewStyle;
  filterWrapper: ViewStyle;
  statusWrapper: ViewStyle;
  tableWrapper: ViewStyle;
  calendar: ViewStyle;
};

const styles: styleTypes = {
  statusWrapper: {
    top: 40,
    left: 0,
    maxWidth: 200,
    position: 'absolute',
  },
  calendar: {
    top: 0,
    right: 0,
    minHeight: 350,
  },
  tableWrapper: {zIndex: -1},
  filterWrapper: {
    position: 'relative',
  },
  tableCellWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing['1'],
  },
  tableHeaderIcon: {
    width: 16,
    height: 16,
    aspectRatio: '1/1',
  },
  tableHeader: {
    fontSize: 18,
    fontWeight: 500,
  },
  linkCellText: {textDecoration: 'underline', fontSize: 16},
  doj: {color: colors.palette.secondaryBlack5, fontSize: 14},
  heading: {
    color: colors.palette.brandBlue,
    fontSize: 32,
    fontWeight: 600,
  },
  container: {
    padding: spacing['4'],
    gap: spacing['4'],
    minHeight: '100%',
  },
  addButton: {
    minWidth: 0,
    maxWidth: 150,
    marginLeft: spacing['2'],
  },
  header: {
    width: '100%',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  headerAction: {
    flexDirection: 'row',
    gap: spacing['2'],
  },
  searchWrapper: {
    backgroundColor: colors.palette.gray,
    paddingHorizontal: spacing['2'],
    borderRadius: corner_radius['rounded-md'],
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing['2'],
    overflow: 'hidden',
    maxWidth: 400,
    width: 400,
    minWidth: 0,
  },
  icon: {
    width: 16,
    height: 16,
  },
  searchInput: {
    width: '100%',
    paddingVertical: spacing['2'],
    outlineColor: colors.palette.gray,
    outlineWidth: 0,
  },
};
